import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  Trabajador,
  CreateTrabajadorRequest,
  RegimenLaboral,
  ModalidadContrato,
  SituacionTrabajador,
  CategoriaTrabajador,
  MotivoCese,
  Area,
  RegimenPensionario,
  ConceptoPlanilla,
} from '../types/trabajador';
import { TrabajadorService } from '../services';

interface TrabajadorState {
  // State
  trabajadores: Trabajador[];
  loading: boolean;
  error: string | null;
  selectedTrabajador: Trabajador | null;

  // Catalogs
  regimenesLaborales: RegimenLaboral[];
  modalidadesContrato: ModalidadContrato[];
  situaciones: SituacionTrabajador[];
  categorias: CategoriaTrabajador[];
  motivosCese: MotivoCese[];
  areas: Area[];
  regimenesPensionarios: RegimenPensionario[];
  conceptosPlanilla: ConceptoPlanilla[];
  catalogsLoaded: boolean;

  // UI State
  dialogOpen: boolean;

  // Actions
  fetchTrabajadores: () => Promise<void>;
  fetchCatalogs: () => Promise<void>;
  saveTrabajador: (request: CreateTrabajadorRequest) => Promise<void>;
  deleteTrabajador: (id: number) => Promise<void>;

  // UI Actions
  setSelectedTrabajador: (trabajador: Trabajador | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

const initialState = {
  trabajadores: [],
  loading: false,
  error: null,
  selectedTrabajador: null,
  regimenesLaborales: [],
  modalidadesContrato: [],
  situaciones: [],
  categorias: [],
  motivosCese: [],
  areas: [],
  regimenesPensionarios: [],
  conceptosPlanilla: [],
  catalogsLoaded: false,
  dialogOpen: false,
};

export const useTrabajadorStore = create<TrabajadorState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchTrabajadores: async () => {
        set({ loading: true, error: null }, false, 'trabajadores/fetch/start');
        try {
          const trabajadores = await TrabajadorService.getAll();
          set({ trabajadores, loading: false }, false, 'trabajadores/fetch/success');
        } catch (error) {
          set(
            { loading: false, error: error instanceof Error ? error.message : 'Error al cargar trabajadores' },
            false,
            'trabajadores/fetch/error'
          );
        }
      },

      fetchCatalogs: async () => {
        if (get().catalogsLoaded) return;
        try {
          const [
            regimenesLaborales,
            modalidadesContrato,
            situaciones,
            categorias,
            motivosCese,
            areas,
            regimenesPensionarios,
            conceptosPlanilla,
          ] = await Promise.all([
            TrabajadorService.getRegimenesLaborales(),
            TrabajadorService.getModalidadesContrato(),
            TrabajadorService.getSituaciones(),
            TrabajadorService.getCategorias(),
            TrabajadorService.getMotivosCese(),
            TrabajadorService.getAreas(),
            TrabajadorService.getRegimenesPensionarios(),
            TrabajadorService.getConceptosPlanilla(),
          ]);
          set({
            regimenesLaborales,
            modalidadesContrato,
            situaciones,
            categorias,
            motivosCese,
            areas,
            regimenesPensionarios,
            conceptosPlanilla,
            catalogsLoaded: true,
          }, false, 'trabajadores/fetchCatalogs/success');
        } catch (error) {
          console.error('Error loading catalogs:', error);
        }
      },

      saveTrabajador: async (request: CreateTrabajadorRequest) => {
        set({ loading: true, error: null }, false, 'trabajadores/save/start');
        try {
          const saved = await TrabajadorService.createWithPersonal(request);
          const isUpdate = !!request.idTrabajador;
          set(
            (state) => ({
              trabajadores: isUpdate
                ? state.trabajadores.map((t) => t.idTrabajador === saved.idTrabajador ? saved : t)
                : [...state.trabajadores, saved],
              loading: false,
              dialogOpen: false,
              selectedTrabajador: null,
            }),
            false,
            isUpdate ? 'trabajadores/update/success' : 'trabajadores/create/success'
          );
          // Refresh to get navigation properties
          get().fetchTrabajadores();
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al guardar');
          set(
            { loading: false, error: errorMessage },
            false,
            'trabajadores/save/error'
          );
          throw error;
        }
      },

      deleteTrabajador: async (id: number) => {
        set({ loading: true, error: null }, false, 'trabajadores/delete/start');
        try {
          await TrabajadorService.delete(id);
          set(
            (state) => ({
              trabajadores: state.trabajadores.filter((t) => t.idTrabajador !== id),
              loading: false,
            }),
            false,
            'trabajadores/delete/success'
          );
        } catch (error) {
          set(
            { loading: false, error: error instanceof Error ? error.message : 'Error al eliminar' },
            false,
            'trabajadores/delete/error'
          );
        }
      },

      setSelectedTrabajador: (trabajador) => {
        set({ selectedTrabajador: trabajador }, false, 'trabajadores/setSelected');
      },

      openDialog: () => {
        set({ dialogOpen: true }, false, 'trabajadores/openDialog');
      },

      closeDialog: () => {
        set({ dialogOpen: false, selectedTrabajador: null }, false, 'trabajadores/closeDialog');
      },

      clearError: () => {
        set({ error: null }, false, 'trabajadores/clearError');
      },
    }),
    { name: 'trabajador-store' }
  )
);
